import { cva, type VariantProps } from 'class-variance-authority'
import { cloneElement, forwardRef, isValidElement, type ReactElement } from 'react'

import { type IconPropsType } from '@/legacy-src/components/icons/Icon'
import { dayjs } from '@/legacy-src/lib/dayjs/day'
import { cn } from '@/legacy-src/lib/utils'

import { Text } from '@/src/domains/design-system/components/Text'
import {
  AppointmentTypeIcon,
  type AppointmentTypeIconProps,
} from '@/src/domains/appointments/components/AppointmentTypeIcon'

const variants = cva(
  'flex h-[3.625rem] items-center gap-1 rounded-xl border-none p-2 ring-offset-background transition-colors hover:bg-petrol-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-petrol-500 disabled:pointer-events-none disabled:bg-sand-400 disabled:text-sand-700',
  {
    variants: {
      variant: {
        planned: 'bg-peach-500 text-white-500',
        free: 'bg-blue-500 text-secondary hover:text-white active:text-white',
        'free-selected': 'bg-petrol-500 text-white-500',
      },
    },
  },
)

export interface ClientBookingSlotButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'slot' | 'children'>,
    VariantProps<typeof variants> {
  startDate: string
  slotType?: AppointmentTypeIconProps['type']
  icon?: ReactElement<IconPropsType>
}

export const ClientBookingSlotButton = forwardRef<HTMLButtonElement, ClientBookingSlotButtonProps>(
  ({ startDate, slotType, variant, icon, className, ...props }, ref) => {
    const date = dayjs(startDate)

    return (
      <button ref={ref} className={cn(variants({ variant, className }))} {...props}>
        {slotType !== undefined ? <AppointmentTypeIcon type={slotType} /> : null}
        <Text variant="sm" weight="bold" asChild>
          <time dateTime={date.toISOString()}>{date.format('LT')}</time>
        </Text>
        {isValidElement(icon)
          ? icon
            ? cloneElement(icon, {
                size: 16,
                className: cn(icon.props.className, {
                  'text-sand-700': props.disabled,
                }),
                color: props.disabled ? undefined : icon.props.color,
              })
            : null
          : null}
      </button>
    )
  },
)

ClientBookingSlotButton.displayName = 'ClientBookingSlotButton'
